import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Wrench, Battery, Smartphone, Cpu, Droplets, ShieldCheck, ArrowRight, Sparkles } from "lucide-react";

const SERVICES = [
  { icon: Smartphone, label: "Zamjena ekrana", desc: "Original i kvalitetne zamjene" },
  { icon: Battery, label: "Zamjena baterije", desc: "Vraćamo izdržljivost mobitela" },
  { icon: Droplets, label: "Tečnost / voda", desc: "Spašavamo mobitele od kvarova" },
  { icon: Cpu, label: "Software & sistem", desc: "Reset, update, oporavak podataka" },
  { icon: Wrench, label: "Konektori & punjenje", desc: "USB, slušalice, zvučnici" },
  { icon: ShieldCheck, label: "Garancija na popravak", desc: "Mirno spavaj nakon servisa" },
];

export default function PhoneRepair() {
  const [broken, setBroken] = useState(true);

  // Loop: 2.8s broken → 1.8s fixed → repeat
  useEffect(() => {
    const t = setInterval(() => setBroken((b) => !b), broken ? 2800 : 1800);
    return () => clearInterval(t);
  }, [broken]);

  return (
    <section
      id="popravak"
      data-testid="repair-section"
      className="relative py-24 lg:py-32 bg-neutral-950 text-white overflow-hidden"
    >
      {/* Background gradients */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#D32F2F] rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-red-900 rounded-full blur-[120px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* LEFT — animated phone */}
          <div className="relative flex justify-center order-2 lg:order-1">
            <PhoneAnimation broken={broken} />
          </div>

          {/* RIGHT — text + services */}
          <div className="order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-xs uppercase tracking-[0.2em] mb-5">
              <Wrench className="w-3.5 h-3.5 text-[#FCA5A5]" />
              Servis mobitela
            </div>

            <h2
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] mb-4"
              style={{ fontFamily: "Cabinet Grotesk" }}
            >
              <AnimatePresence mode="wait">
                {broken ? (
                  <motion.span
                    key="broken"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.35 }}
                    className="block"
                  >
                    Ups! Razbili ste{" "}
                    <span className="text-[#D32F2F]">mobitel</span>?
                  </motion.span>
                ) : (
                  <motion.span
                    key="fixed"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.35 }}
                    className="block"
                  >
                    Bez panike —{" "}
                    <span className="text-[#FCA5A5]">mi smo rješenje.</span>
                  </motion.span>
                )}
              </AnimatePresence>
            </h2>

            <p className="text-white/70 text-lg leading-relaxed mb-8 max-w-lg">
              Pukotina na ekranu, slaba baterija, problem sa punjenjem ili sistem?
              U MOBY SHOP-u brzo dijagnosticiramo i popravljamo mobitele svih marki —
              uz <strong className="text-white">garanciju</strong> na izvedeni posao.
            </p>

            {/* Services grid */}
            <div className="grid sm:grid-cols-2 gap-3 mb-8">
              {SERVICES.map((s, i) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  data-testid={`repair-service-${i}`}
                  className="group flex items-start gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#D32F2F]/50 transition-all"
                >
                  <div className="w-9 h-9 grid place-items-center bg-[#D32F2F] rounded-lg shrink-0 group-hover:scale-110 transition-transform">
                    <s.icon className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-white text-sm">{s.label}</div>
                    <div className="text-xs text-white/60 leading-tight mt-0.5">{s.desc}</div>
                  </div>
                </motion.div>
              ))}
            </div>

            <a
              href="#kontakt"
              data-testid="repair-cta"
              className="group inline-flex items-center gap-3 bg-[#D32F2F] hover:bg-[#B71C1C] text-white px-7 py-3.5 rounded-full font-semibold transition-all hover:shadow-2xl hover:shadow-red-500/30"
            >
              Donesi mobitel u radnju
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============== Animated phone illustration ============== */

function PhoneAnimation({ broken }) {
  return (
    <div className="relative w-full max-w-sm aspect-[9/16]">
      <motion.div
        animate={{
          rotate: broken ? [-2, 2, -2] : 0,
          y: broken ? [0, -4, 0] : 0,
        }}
        transition={{
          duration: broken ? 0.3 : 0.5,
          repeat: broken ? Infinity : 0,
          ease: "easeInOut",
        }}
        className="relative w-full h-full"
      >
        {/* Phone body */}
        <div className="absolute inset-0 bg-neutral-900 rounded-[42px] border-[3px] border-neutral-800 shadow-2xl shadow-black/60">
          {/* Notch */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-full" />
          {/* Screen */}
          <div className="absolute inset-3 top-12 bottom-3 rounded-[32px] overflow-hidden bg-gradient-to-br from-neutral-800 via-neutral-900 to-black">
            <AnimatePresence mode="wait">
              {broken ? (
                <motion.div
                  key="cracked"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                  className="absolute inset-0"
                >
                  {/* dim "freeze" content */}
                  <div className="absolute inset-0 bg-gradient-to-b from-red-900/20 to-black/40" />
                  {/* Glitch lines */}
                  <div className="absolute inset-0 opacity-30">
                    {[10, 30, 55, 70, 88].map((p) => (
                      <div
                        key={p}
                        className="absolute left-0 right-0 h-px bg-white/40"
                        style={{ top: `${p}%` }}
                      />
                    ))}
                  </div>
                  {/* SVG cracks */}
                  <svg
                    viewBox="0 0 200 360"
                    preserveAspectRatio="none"
                    className="absolute inset-0 w-full h-full"
                  >
                    <defs>
                      <linearGradient id="crack" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#fff" stopOpacity="0.9" />
                        <stop offset="100%" stopColor="#fff" stopOpacity="0.4" />
                      </linearGradient>
                    </defs>
                    {CRACK_PATHS.map((d, i) => (
                      <motion.path
                        key={i}
                        d={d}
                        stroke="url(#crack)"
                        strokeWidth="1.2"
                        fill="none"
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ pathLength: 1, opacity: 1 }}
                        transition={{
                          duration: 0.6,
                          delay: i * 0.08,
                          ease: "easeOut",
                        }}
                      />
                    ))}
                    {/* Impact star */}
                    <motion.circle
                      cx="105"
                      cy="160"
                      r="3"
                      fill="#fff"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: [0, 2, 1], opacity: [0, 1, 0.6] }}
                      transition={{ duration: 0.5 }}
                    />
                  </svg>
                  {/* Status text */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                    className="absolute bottom-12 left-0 right-0 text-center px-4"
                  >
                    <div className="text-red-400 text-xs uppercase tracking-[0.25em] font-bold animate-pulse">
                      ⚠ Greška sistema
                    </div>
                  </motion.div>
                </motion.div>
              ) : (
                <motion.div
                  key="fixed"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="absolute inset-0 bg-gradient-to-br from-[#D32F2F] via-red-700 to-[#B71C1C] grid place-items-center"
                >
                  {/* Sparkles burst */}
                  {[...Array(8)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute"
                      initial={{
                        scale: 0,
                        x: 0,
                        y: 0,
                        opacity: 1,
                      }}
                      animate={{
                        scale: [0, 1, 0],
                        x: Math.cos((i / 8) * Math.PI * 2) * 90,
                        y: Math.sin((i / 8) * Math.PI * 2) * 90,
                        opacity: [1, 1, 0],
                      }}
                      transition={{ duration: 0.9, delay: 0.1 + i * 0.04 }}
                    >
                      <Sparkles className="w-4 h-4 text-yellow-200" />
                    </motion.div>
                  ))}
                  {/* Checkmark */}
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{
                      type: "spring",
                      stiffness: 200,
                      damping: 14,
                      delay: 0.15,
                    }}
                    className="relative"
                  >
                    <div className="w-24 h-24 bg-white rounded-full grid place-items-center shadow-2xl">
                      <svg viewBox="0 0 24 24" className="w-12 h-12 text-[#D32F2F]" fill="none">
                        <motion.path
                          d="M5 13l4 4L19 7"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          initial={{ pathLength: 0 }}
                          animate={{ pathLength: 1 }}
                          transition={{ duration: 0.4, delay: 0.4 }}
                        />
                      </svg>
                    </div>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                    className="absolute bottom-12 left-0 right-0 text-center px-4"
                  >
                    <div className="text-white text-sm font-bold tracking-wide">
                      Popravljeno!
                    </div>
                    <div className="text-white/80 text-[10px] uppercase tracking-[0.2em] mt-1">
                      MOBY SHOP servis
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {/* Side buttons */}
          <div className="absolute -left-1 top-24 w-1 h-10 bg-neutral-700 rounded-l" />
          <div className="absolute -left-1 top-40 w-1 h-16 bg-neutral-700 rounded-l" />
          <div className="absolute -right-1 top-32 w-1 h-20 bg-neutral-700 rounded-r" />
        </div>
      </motion.div>

      {/* "Status" badge that swaps */}
      <AnimatePresence mode="wait">
        {broken ? (
          <motion.div
            key="b-badge"
            initial={{ opacity: 0, scale: 0.7, rotate: -10 }}
            animate={{ opacity: 1, scale: 1, rotate: -8 }}
            exit={{ opacity: 0, scale: 0.7 }}
            transition={{ duration: 0.4 }}
            className="absolute -top-6 -right-2 bg-red-600 text-white text-xs font-bold px-4 py-2 rounded-xl shadow-2xl shadow-red-900/50 uppercase tracking-wider"
          >
            Ups!
          </motion.div>
        ) : (
          <motion.div
            key="f-badge"
            initial={{ opacity: 0, scale: 0.7, rotate: 10 }}
            animate={{ opacity: 1, scale: 1, rotate: 6 }}
            exit={{ opacity: 0, scale: 0.7 }}
            transition={{ duration: 0.4 }}
            className="absolute -top-6 -right-2 bg-green-500 text-white text-xs font-bold px-4 py-2 rounded-xl shadow-2xl shadow-green-900/50 uppercase tracking-wider"
          >
            Popravljeno ✓
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* SVG path data for cracks radiating from center-ish */
const CRACK_PATHS = [
  "M105 160 L60 90 L40 60",
  "M105 160 L150 100 L180 70",
  "M105 160 L70 220 L40 280",
  "M105 160 L160 230 L190 290",
  "M105 160 L100 80 L95 30",
  "M105 160 L110 250 L115 330",
  "M60 90 L30 110",
  "M150 100 L180 130",
  "M70 220 L40 230",
  "M160 230 L195 245",
];
